import { useState, useRef, useEffect } from "react";
import { useToolOpener } from "@/lib/dockBus";
import { MessageCircle, X, Send, Loader2, Briefcase, ArrowLeft, Mic, Square, TrendingUp, CalendarClock, Navigation } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";

const CHIPS = [
  { label: "Find a job", to: "/jobs" },
  { label: "Sell something", to: "/market" },
  { label: "Order food", to: "/food" },
  { label: "Free courses", to: "/learn" },
];

const MAPS_RE = /(https?:\/\/(?:www\.)?google\.com\/maps\/[^\s)]+)/i;

function MessageBody({ content }) {
  const match = content.match(MAPS_RE);
  if (!match) return <>{content}</>;
  const url = match[1];
  const text = content.replace(url, "").trim();
  return (
    <>
      {text && <span className="block whitespace-pre-wrap">{text}</span>}
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid="chat-directions-btn"
        className="inline-flex items-center gap-1.5 mt-2 text-sm font-semibold text-white bg-pula-green hover:bg-pula-greenhover rounded-full px-3 py-1.5 no-underline transition-colors">
        <Navigation className="w-4 h-4" /> Open directions
      </a>
    </>
  );
}

export const ChatWidget = () => {
  const { user } = useAuth();
  const { language } = useSettings();
  const navigate = useNavigate();
  const firstName = (user?.name || "").split(" ")[0] || "";
  const [open, setOpen] = useState(false);
  useToolOpener("halle", () => setOpen(true));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showInquiry, setShowInquiry] = useState(false);
  const [inq, setInq] = useState({ name: user?.name || "", email: user?.email || "", category: "general", message: "" });
  const [sendingInq, setSendingInq] = useState(false);
  const [recording, setRecording] = useState(false);
  const [config, setConfig] = useState({ calendly_url: "" });
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && !config._loaded) {
      api.get("/reception/config").then(({ data }) => setConfig({ ...data, _loaded: true })).catch(() => setConfig({ calendly_url: "", _loaded: true }));
    }
  }, [open]); // eslint-disable-line

  const investorBrief = async () => {
    setLoading(true);
    setMessages((m) => [...m, { role: "user", content: "Tell me about Pula's growth & compliance (for investors/partners)." }]);
    try {
      const { data } = await api.get("/reception/investor-brief");
      const s = data.stats;
      const text = `Here's a quick, honest snapshot for partners & investors:\n\n📈 Community\n• ${s.members_total} members (${s.members_month} joined this month)\n• ${s.listings} marketplace listings · ${s.jobs} jobs · ${s.restaurants} kitchens\n• ${s.courses} free courses on Pula Learn\n\n🛡️ Our compliance journey\n${data.compliance_journey}\n\nFor a full conversation, tap "Leave a message for the Office of the CEO" below and we'll be in touch.`;
      setMessages((m) => [...m, { role: "assistant", content: text }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I couldn't load our stats just now — please leave a message for the Office of the CEO and we'll share our latest figures." }]);
    } finally { setLoading(false); }
  };

  const toggleRecord = async () => {
    if (recording) { recRef.current?.stop(); return; }
    if (typeof MediaRecorder === "undefined") { toast.error("Your browser can't record — please type instead."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm", "audio/mp4"].find((t) => MediaRecorder.isTypeSupported?.(t)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        const fd = new FormData();
        fd.append("audio", blob, "voice.webm");
        setLoading(true);
        try {
          const { data } = await api.post("/reception/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setLoading(false);
          if (data.text) send(data.text);
          else toast.error("I couldn't hear that clearly — please try again or type.");
        } catch { setLoading(false); toast.error("Voice failed — please type instead."); }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch { toast.error("Please allow microphone access to speak."); }
  };

  useEffect(() => {
    if (open && messages.length === 0) {
      const greeting = firstName
        ? `Welcome back, ${firstName} 👋 I'm Halle, your friendly guide. Ask me anything about Pula — in any language. How can I help you today?`
        : `Hello 👋 I'm Halle, your friendly guide. Ask me anything about Pula — in any language. How can I help you today?`;
      setMessages([{ role: "assistant", content: greeting }]);
      if (language && language !== "en") {
        api.post("/translate/ui", { target_lang: language, texts: [greeting] })
          .then(({ data }) => {
            const tr = (data.translations || {})[greeting];
            if (tr) setMessages((m) => (m.length === 1 && m[0].role === "assistant" ? [{ role: "assistant", content: tr }] : m));
          })
          .catch(() => {});
      }
    }
  }, [open]); // eslint-disable-line

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open, showInquiry]);

  const send = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/reception/chat", { message: text, session_id: sessionId, name: user?.name || "", lang: language || "en" });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. You can leave a message for the Office of the CEO below and a real person will reply within 24 hours." }]);
    } finally { setLoading(false); }
  };

  const submitInquiry = async () => {
    if (!inq.name.trim() || !inq.email.trim() || inq.message.trim().length < 3) { toast.error("Please add your name, email and a short message."); return; }
    setSendingInq(true);
    try {
      await api.post("/reception/inquiry", { ...inq, session_id: sessionId });
      toast.success("Thank you — your message is on its way to the Office of the CEO.");
      setShowInquiry(false);
      setMessages((m) => [...m, { role: "assistant", content: "Thank you 🌿 I've passed your message to the Office of the CEO — someone will be in touch soon." }]);
      setInq({ name: user?.name || "", email: user?.email || "", category: "general", message: "" });
    } catch { toast.error("Could not send — please try again."); }
    finally { setSendingInq(false); }
  };

  return (
    <>
      {!open && (
        <button data-testid="chat-widget-open" onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full overflow-hidden ring-2 ring-pula-green shadow-lg hover:scale-105 transition-transform">
          <img src="/halle.jpg" alt="Halle" className="w-full h-full object-cover" />
        </button>
      )}
      {open && (
        <div data-testid="chat-widget" className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col pula-card overflow-hidden">
          <div className="bg-pula-green text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {showInquiry && <button onClick={() => setShowInquiry(false)} className="p-1 hover:bg-white/20 rounded"><ArrowLeft className="w-4 h-4" /></button>}
              <img src="/halle.jpg" alt="Halle" className="w-9 h-9 rounded-full object-cover ring-2 ring-white/40 shrink-0" />
              <div>
                <p className="font-heading font-bold text-sm">Halle</p>
                <p className="text-xs text-white/80">Your 24/7 Pula concierge · any language</p>
              </div>
            </div>
            <button data-testid="chat-widget-close" onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded transition-colors"><X className="w-5 h-5" /></button>
          </div>

          {showInquiry ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-pula-bg2" data-testid="ceo-inquiry-form">
              <p className="text-sm font-body text-pula-ink">Leave a message for the <b>Office of the CEO</b> (press, partnerships or formal enquiries):</p>
              <input data-testid="inq-name" value={inq.name} onChange={(e) => setInq({ ...inq, name: e.target.value })} placeholder="Your name" className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--pula-border)] text-sm font-body outline-none focus:ring-2 focus:ring-pula-green" />
              <input data-testid="inq-email" value={inq.email} onChange={(e) => setInq({ ...inq, email: e.target.value })} placeholder="Your email" className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--pula-border)] text-sm font-body outline-none focus:ring-2 focus:ring-pula-green" />
              <select data-testid="inq-category" value={inq.category} onChange={(e) => setInq({ ...inq, category: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--pula-border)] text-sm font-body outline-none focus:ring-2 focus:ring-pula-green">
                <option value="general">General</option>
                <option value="press">Press / Media</option>
                <option value="partnership">Partnership</option>
                <option value="investor">Investor</option>
              </select>
              <textarea data-testid="inq-message" value={inq.message} onChange={(e) => setInq({ ...inq, message: e.target.value })} placeholder="How can we help?" rows={4} className="w-full px-3 py-2 rounded-lg bg-white border border-[var(--pula-border)] text-sm font-body outline-none focus:ring-2 focus:ring-pula-green resize-none" />
              <button data-testid="inq-submit" onClick={submitInquiry} disabled={sendingInq} className="w-full py-2.5 rounded-full bg-pula-green hover:bg-pula-greenhover text-white text-sm font-semibold grid place-items-center disabled:opacity-50">
                {sendingInq ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send to the Office of the CEO"}
              </button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-pula-bg2">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div data-no-translate className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm font-body whitespace-pre-wrap ${m.role === "user" ? "bg-pula-green text-white rounded-br-sm" : "bg-white text-pula-ink rounded-bl-sm border border-[var(--pula-border)]"}`}>{m.role === "assistant" ? <MessageBody content={m.content} /> : m.content}</div>
                  </div>
                ))}
                {messages.length <= 1 && (
                  <div className="flex flex-wrap gap-1.5 pt-1" data-testid="chat-chips">
                    {CHIPS.map((c) => (
                      <button key={c.label} onClick={() => { navigate(c.to); setOpen(false); }} className="px-2.5 py-1 rounded-full bg-white border border-[var(--pula-border)] text-xs font-body text-pula-inksoft hover:border-pula-green hover:text-pula-green transition-colors">{c.label}</button>
                    ))}
                    <button data-testid="chat-investor" onClick={investorBrief} className="px-2.5 py-1 rounded-full bg-white border border-pula-olive/40 text-xs font-body text-pula-olive hover:bg-pula-olive/10 transition-colors flex items-center gap-1"><TrendingUp className="w-3 h-3" /> For investors</button>
                    {config.calendly_url && (
                      <a data-testid="chat-book" href={config.calendly_url} target="_blank" rel="noopener noreferrer" className="px-2.5 py-1 rounded-full bg-white border border-pula-green/40 text-xs font-body text-pula-green hover:bg-pula-green/10 transition-colors flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Book a meeting</a>
                    )}
                  </div>
                )}
                {loading && (
                  <div className="flex justify-start"><div className="bg-white border border-[var(--pula-border)] rounded-2xl px-3 py-2"><Loader2 className="w-4 h-4 animate-spin text-pula-green" /></div></div>
                )}
                <div ref={endRef} />
              </div>
              <button data-testid="open-ceo-inquiry" onClick={() => setShowInquiry(true)}
                className="flex items-center justify-center gap-2 bg-pula-highlight/20 text-pula-olive text-xs font-medium py-2 hover:bg-pula-highlight/30 transition-colors">
                <Briefcase className="w-3.5 h-3.5" /> Leave a message for the Office of the CEO
              </button>
              <div className="p-2 border-t border-[var(--pula-border)] flex items-center gap-2 bg-white">
                <button data-testid="chat-mic" onClick={toggleRecord} title="Speak"
                  className={`w-9 h-9 rounded-full grid place-items-center transition-colors shrink-0 ${recording ? "bg-red-500 text-white animate-pulse" : "bg-pula-bg2 text-pula-inksoft hover:text-pula-green"}`}>
                  {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
                <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={recording ? "Listening…" : "Type or speak, any language…"} className="flex-1 px-3 py-2 rounded-full bg-pula-bg2 text-sm font-body outline-none focus:ring-2 focus:ring-pula-green" />
                <button data-testid="chat-send" onClick={() => send()} disabled={loading} className="w-9 h-9 rounded-full bg-pula-green hover:bg-pula-greenhover text-white grid place-items-center transition-colors disabled:opacity-50 shrink-0"><Send className="w-4 h-4" /></button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};
